import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Copy } from "lucide-react";
import FunnelLayout from "@/v2/components/FunnelLayout";
import GlassCard from "@/v2/components/GlassCard";
import DiagnosisBadge from "@/v2/components/DiagnosisBadge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { loadResult, type StoredRealityCheck } from "@/v2/lib/realityCheckApi";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

async function claimRc(rcId: string): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !rcId) return;
    await supabase
      .from("v2_reality_checks")
      .update({ user_id: user.id })
      .eq("id", rcId)
      .is("user_id", null);
  } catch {
    // silent — non bloccare il flusso
  }
}

const VERDICT_STYLES: Record<
  "CONTINUA" | "ITERA" | "RIPENSA",
  { card: string; text: string }
> = {
  CONTINUA: {
    card: "border-green-400/40 shadow-[0_0_0_1px_rgba(74,222,128,0.15)]",
    text: "text-green-400",
  },
  ITERA: {
    card: "border-amber-400/40 shadow-[0_0_0_1px_rgba(251,191,36,0.15)]",
    text: "text-amber-400",
  },
  RIPENSA: {
    card: "border-red-400/30 shadow-[0_0_0_1px_rgba(248,113,113,0.12)]",
    text: "text-red-400",
  },
};

const READINESS_COLOR: Record<"Alto" | "Medio" | "Basso", string> = {
  Alto: "text-green-400",
  Medio: "text-amber-400",
  Basso: "text-slate-400",
};

export default function RealityCheckResult() {
  const navigate = useNavigate();
  const { user, signIn, signUp } = useAuth();
  const [data, setData] = useState<StoredRealityCheck | null>(null);
  const [copied, setCopied] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState<"signin" | "signup">("signin");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");
  const [signUpName, setSignUpName] = useState("");
  const [signUpEmail, setSignUpEmail] = useState("");
  const [signUpPassword, setSignUpPassword] = useState("");
  const canCopy =
    typeof navigator !== "undefined" &&
    !!navigator.clipboard &&
    typeof navigator.clipboard.writeText === "function";

  useEffect(() => {
    const d = loadResult();
    if (!d) {
      navigate("/v2/reality-check", { replace: true });
      return;
    }
    setData(d);
    try {
      const savedEmail = sessionStorage.getItem("v2_rc_email");
      if (savedEmail) {
        setSignInEmail(savedEmail);
        setSignUpEmail(savedEmail);
      }
    } catch {}
  }, [navigate]);


  if (!data) return null;
  const r = data.result;
  const verdictStyle = r.verdict ? VERDICT_STYLES[r.verdict] : null;

  const handleCopyScript = async () => {
    if (!r.contact_script || !canCopy) return;
    try {
      await navigator.clipboard.writeText(r.contact_script);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* silent */
    }
  };

  const proceedToPricing = async () => {
    await claimRc(data.id);
    navigate("/v2/pricing");
  };

  const handleSprintCta = async () => {
    if (user) {
      await proceedToPricing();
    } else {
      setAuthError(null);
      setAuthOpen(true);
    }
  };

  const handleSignInSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setAuthLoading(true);
    const { error } = await signIn(signInEmail, signInPassword);
    setAuthLoading(false);
    if (error) {
      setAuthError(error.message || "Errore durante l'accesso");
      return;
    }
    setAuthOpen(false);
    await proceedToPricing();
  };

  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setAuthLoading(true);
    const { error } = await signUp(signUpEmail, signUpPassword, signUpName);
    setAuthLoading(false);
    if (error) {
      setAuthError(error.message || "Errore durante la registrazione");
      return;
    }
    setAuthOpen(false);
    await proceedToPricing();
  };


  return (
    <FunnelLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <header className="space-y-3">
          <p className="text-xs uppercase tracking-widest text-secondary">Diagnosi</p>
          <h1 className="text-3xl md:text-4xl font-bold leading-tight">{r.headline}</h1>
          {r.prudent_note && (
            <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
              {r.prudent_note}
            </div>
          )}
        </header>

        {/* Sezione 1 — Verdetto */}
        {r.verdict && verdictStyle && (
          <GlassCard className={`space-y-1 ${verdictStyle.card}`}>
            <p className={`text-xs uppercase tracking-widest ${verdictStyle.text}`}>
              Verdetto
            </p>
            <p className={`text-2xl font-bold tracking-widest ${verdictStyle.text}`}>
              {r.verdict}
            </p>
            {r.verdict_reason && (
              <p className="text-sm text-slate-200 mt-2">{r.verdict_reason}</p>
            )}
            {r.readiness_level && (
              <p className="text-xs mt-3">
                <span className="text-slate-400">Livello di prontezza: </span>
                <span className={READINESS_COLOR[r.readiness_level]}>
                  {r.readiness_level}
                </span>
                <span className="text-slate-500"> (indicazione orientativa)</span>
              </p>
            )}
          </GlassCard>
        )}

        {/* Banner versione precedente */}
        {!r.verdict && (
          <GlassCard className="space-y-3">
            <p className="text-sm text-slate-200">
              Questo risultato è stato generato con una versione precedente.
              Rifai il Reality Check per ricevere la diagnosi completa.
            </p>
            <Link
              to="/v2/reality-check"
              className="inline-block text-sm text-secondary hover:underline"
            >
              Rifai il Reality Check →
            </Link>
          </GlassCard>
        )}

        {/* Sezione 2 — Rischio */}
        {r.failure_reason && (
          <GlassCard className="space-y-2">
            <h3 className="text-xs uppercase tracking-wide text-secondary">
              {r.verdict === "CONTINUA"
                ? "Rischio principale da non ignorare"
                : "Perché potrebbe non funzionare"}
            </h3>
            <p className="text-sm text-slate-200">{r.failure_reason}</p>
          </GlassCard>
        )}

        {/* Sezione 3 — Missione 48h */}
        <GlassCard className="space-y-3">
          <div className="flex items-center">
            <span className="text-xs uppercase tracking-wide text-secondary">
              La tua missione nei prossimi 2 giorni
            </span>
            <span className="text-xs bg-secondary/20 text-secondary rounded-full px-2 py-0.5 ml-2">
              48h
            </span>
          </div>
          <div>
            <p className="font-medium text-white text-base">{r.experiment_48h}</p>
            <p className="text-sm text-slate-300 mt-1">{r.why_this_experiment}</p>
          </div>

          {r.contact_script && (
            <div>
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-secondary">
                  Script da usare
                </span>
                {canCopy && (
                  <button
                    type="button"
                    onClick={handleCopyScript}
                    className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white transition-colors"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    {copied ? "Copiato ✓" : "Copia"}
                  </button>
                )}
              </div>
              <div className="bg-white/5 rounded-xl p-4 mt-2 text-sm text-slate-200 whitespace-pre-wrap">
                {r.contact_script}
              </div>
            </div>
          )}

          <p className="mt-3 text-xs text-amber-300/80">
            Successo minimo: {r.minimum_success_threshold}
          </p>
        </GlassCard>

        {/* Sezione 4 — Diagnosi (accordion) */}
        <GlassCard className="py-2">
          <Accordion type="single" collapsible defaultValue={undefined}>
            <AccordionItem value="diagnosi" className="border-0">
              <AccordionTrigger className="text-sm font-medium hover:no-underline">
                Dettaglio diagnosi
              </AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-3">
                  {r.diagnosis.map((d) => (
                    <li
                      key={d.area}
                      className="flex flex-col gap-1.5 border-b border-white/5 pb-3 last:border-0 last:pb-0"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-medium text-white">{d.area}</span>
                        <DiagnosisBadge level={d.level} />
                      </div>
                      <p className="text-sm text-slate-300">{d.note}</p>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 pt-3 border-t border-white/5">
                  <span className="text-slate-400 text-sm">Rischio principale: </span>
                  <span className="text-slate-200 text-sm">{r.main_risk}</span>
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </GlassCard>

        {/* Sezione 5 — CTA */}
        <div className="space-y-3">
          <p className="text-sm text-slate-400 mt-2">
            Hai 48 ore per fare questo test. Se vuoi un protocollo guidato per i 7
            giorni successivi:
          </p>
          <div className="flex justify-center pt-2">
            <button
              type="button"
              onClick={handleSprintCta}
              className="group inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-200 font-body px-6 py-3 text-sm md:text-base text-primary-foreground bg-gradient-to-r from-[#FF6B47] to-[#FF8A6B] shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.55)] hover:shadow-[0_18px_44px_-12px_hsl(var(--primary)/0.7)] hover:scale-[1.02] active:scale-[0.99]"
            >
              <span>Inizia lo Sprint — da 49€</span>
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 pt-3 text-sm text-slate-400">
            <span className="inline-flex items-center">
              <CheckCircle2 className="h-4 w-4 text-secondary shrink-0 mr-1.5" />
              7 giorni
            </span>
            <span className="inline-flex items-center">
              <CheckCircle2 className="h-4 w-4 text-secondary shrink-0 mr-1.5" />
              protocollo guidato
            </span>
            <span className="inline-flex items-center">
              <CheckCircle2 className="h-4 w-4 text-secondary shrink-0 mr-1.5" />
              verdetto basato su dati
            </span>
          </div>
        </div>
      </div>

      <Dialog open={authOpen} onOpenChange={setAuthOpen}>
        <DialogContent className="bg-slate-950/98 backdrop-blur-xl border border-white/10 text-slate-100 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Salva il tuo piano e inizia</DialogTitle>
            <DialogDescription className="text-slate-400">
              Crea un account per collegare il tuo Reality Check allo Sprint
            </DialogDescription>
          </DialogHeader>

          <Tabs value={authTab} onValueChange={(v) => { setAuthTab(v as "signin" | "signup"); setAuthError(null); }} className="mt-2">
            <TabsList className="grid w-full grid-cols-2 bg-white/5 border border-white/10">
              <TabsTrigger value="signin" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400">Accedi</TabsTrigger>
              <TabsTrigger value="signup" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400">Registrati</TabsTrigger>
            </TabsList>

            <TabsContent value="signin" className="mt-4">
              <form onSubmit={handleSignInSubmit} className="space-y-3">
                <input
                  type="email"
                  required
                  placeholder="Email"
                  value={signInEmail}
                  onChange={(e) => setSignInEmail(e.target.value)}
                  className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-secondary"
                />
                <input
                  type="password"
                  required
                  placeholder="Password"
                  value={signInPassword}
                  onChange={(e) => setSignInPassword(e.target.value)}
                  className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-secondary"
                />
                {authError && <p className="text-sm text-red-400">{authError}</p>}
                <button
                  type="submit"
                  disabled={authLoading}
                  className="w-full rounded-lg bg-gradient-to-r from-[#FF6B47] to-[#FF8A6B] text-primary-foreground font-semibold py-2 text-sm disabled:opacity-50"
                >
                  {authLoading ? "Accesso..." : "Accedi"}
                </button>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="mt-4">
              <form onSubmit={handleSignUpSubmit} className="space-y-3">
                <input
                  type="text"
                  required
                  placeholder="Nome"
                  value={signUpName}
                  onChange={(e) => setSignUpName(e.target.value)}
                  className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-secondary"
                />
                <input
                  type="email"
                  required
                  placeholder="Email"
                  value={signUpEmail}
                  onChange={(e) => setSignUpEmail(e.target.value)}
                  className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-secondary"
                />
                <input
                  type="password"
                  required
                  minLength={6}
                  placeholder="Password (min. 6 caratteri)"
                  value={signUpPassword}
                  onChange={(e) => setSignUpPassword(e.target.value)}
                  className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-secondary"
                />
                {authError && <p className="text-sm text-red-400">{authError}</p>}
                <button
                  type="submit"
                  disabled={authLoading}
                  className="w-full rounded-lg bg-gradient-to-r from-[#FF6B47] to-[#FF8A6B] text-primary-foreground font-semibold py-2 text-sm disabled:opacity-50"
                >
                  {authLoading ? "Creazione..." : "Crea account"}
                </button>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </FunnelLayout>
  );
}
